import { useState } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Loader2, Zap, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../services/api';

export const ShippingRateTester = ({ storeId, disabled }) => {
  const [pincode, setPincode] = useState('');
  const [weight, setWeight] = useState('0.5');
  const [cod, setCod] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (pincode.length !== 6) return toast.error('Enter a 6-digit delivery pincode');
    setRunning(true);
    setResult(null);
    try {
      const res = await api.post(`/ecommerce/store-settings/${storeId}/test-shipping-rate`, {
        delivery_pincode: pincode, weight_kg: parseFloat(weight) || 0.5, cod,
      });
      setResult(res.data);
      if (res.data.success) toast.success(`Cheapest: ${res.data.cheapest.courier_name} ₹${res.data.cheapest.freight_charge}`);
      else toast.error(res.data.error || 'No rates');
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      setResult({ success: false, error: detail, couriers: [] });
      toast.error(detail);
    } finally { setRunning(false); }
  };

  return (
    <div className="mt-4 rounded-lg border border-orange-200 bg-orange-50/50 p-4 space-y-3" data-testid="sr-rate-tester">
      <div className="font-medium text-sm text-orange-900">Test live shipping rate</div>
      <p className="text-[11px] text-gray-600">
        Uses the saved Shiprocket login + pickup pincode exactly like checkout does. Save first if you changed credentials.
        {disabled && <span className="text-red-600 font-medium"> Shiprocket email & password are not saved yet.</span>}
      </p>
      <div className="grid grid-cols-[1fr_120px_auto_auto] gap-2 items-end">
        <div>
          <Label className="text-xs">Delivery pincode</Label>
          <Input value={pincode} onChange={(e) => setPincode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="e.g. 560001" data-testid="sr-test-pincode" />
        </div>
        <div>
          <Label className="text-xs">Weight (kg)</Label>
          <Input type="number" step="0.1" min="0.1" value={weight} onChange={(e) => setWeight(e.target.value)} data-testid="sr-test-weight" />
        </div>
        <label className="flex items-center gap-1.5 text-xs pb-2.5">
          <input type="checkbox" checked={cod} onChange={(e) => setCod(e.target.checked)} data-testid="sr-test-cod" /> COD
        </label>
        <Button type="button" size="sm" onClick={run} disabled={running || disabled} className="bg-orange-600 hover:bg-orange-700" data-testid="sr-test-rate-btn">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} <span className="ml-1">Get rates</span>
        </Button>
      </div>
      {result && (
        <div className={`rounded-md border p-3 text-sm ${result.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`} data-testid="sr-test-result">
          <div className="flex items-center gap-2 font-medium">
            {result.success ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-red-600" />}
            {result.success
              ? `${result.couriers.length} couriers from ${result.pickup_pincode} → ${pincode} @ ${result.weight_kg} kg`
              : (result.error || 'Rate lookup failed')}
          </div>
          {result.success && (
            <table className="w-full text-xs mt-2">
              <thead><tr className="text-left text-gray-500"><th className="py-1">Courier</th><th>Freight</th><th>ETA (days)</th><th>Rating</th></tr></thead>
              <tbody>
                {result.couriers.slice(0, 8).map((c) => (
                  <tr key={c.courier_id} className="border-t border-green-100">
                    <td className="py-1">{c.courier_name}</td>
                    <td>₹{Number(c.freight_charge).toFixed(2)}</td>
                    <td>{c.estimated_delivery_days || c.etd || '—'}</td>
                    <td>{c.rating ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};
